import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import {
  ConnectedTableComponent,
  IconButtonColumn,
  PaginationConfig,
  TableColumn,
  TextColumn,
} from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { POLICY_DIMENSIONS_CONFIG } from '../../config/policy-dimensions.config';
import { DeleteDimensionDialog, DeleteElementDialog, DimensionDialog, ElementDialog } from '../../dialogs';
import { PolicyService } from '../../services/policy.service';

interface Dimension {
  id: string;
  title: string;
  description?: string;
  isScalar: boolean;
  isPricing?: boolean;
  isSystem?: boolean;
  elementsLocked?: boolean;
  sortOrder?: number;
  created: string;
  updated: string;
}

interface Element {
  id: string;
  dimensionId: string;
  title: string;
  description?: string;
  sortOrder?: number;
  lowerBound?: number;
  upperBound?: number;
  parentId?: string | null;
  unsummedOffset?: boolean;
  includeUpstreamDistributorFees?: boolean;
  isNet?: boolean;
  isOneTime?: boolean;
  created: string;
  updated: string;
}

interface DimensionsMeta {
  totalItems: number;
}

interface ElementsMeta {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
}

interface ElementsResponse {
  items: Element[];
  meta?: Partial<ElementsMeta>;
}

type ElementsLoadResult =
  | { status: 'cleared' }
  | { status: 'blocked'; pagination: { page: number; limit: number } }
  | { status: 'loaded'; response: ElementsResponse; pagination: { page: number; limit: number } }
  | { status: 'error'; error: unknown; pagination: { page: number; limit: number } };

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-dimensions-page',
  templateUrl: './policy-dimensions.page.html',
  standalone: false,
})
export class PolicyDimensionsPage implements OnInit, OnDestroy {
  constructor(
    private policySvc: PolicyService,
    private securitySvc: SecurityService,
    private toastSvc: ToastService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  @ViewChild('dimensionDialog') dimensionDialog!: DimensionDialog;
  @ViewChild('elementDialog') elementDialog!: ElementDialog;
  @ViewChild('deleteDimensionDialog') deleteDimensionDialog!: DeleteDimensionDialog;
  @ViewChild('deleteElementDialog') deleteElementDialog!: DeleteElementDialog;
  @ViewChild('dimensionsTable') dimensionsTable!: ConnectedTableComponent<Dimension>;

  // Config
  readonly config = POLICY_DIMENSIONS_CONFIG;

  policy$ = this.policySvc.getPolicyObservable();

  private destroy$ = new Subject<void>();
  private readonly currentPolicyId$ = new BehaviorSubject<string | null>(null);
  private readonly elementsRefresh$ = new BehaviorSubject<number>(0);

  // Currently selected dimension
  selectedDimension$ = new BehaviorSubject<Dimension | null>(null);

  // Using strings instead of booleans because vs-select treats false as falsy
  dimensionTypeFilter: 'all' | 'scalar' | 'categorical' = 'all';

  // Elements for the selected dimension
  elements$ = new BehaviorSubject<Element[]>([]);
  loadingElements$ = new BehaviorSubject<boolean>(false);
  elementSearchTerm$ = new BehaviorSubject<string>('');
  elementSearchInputValue = '';
  private readonly elementSearchInput$ = new Subject<string>();
  private readonly elementPaginationRequest$ = new BehaviorSubject<{ page: number; limit: number }>({
    page: 1,
    limit: 10,
  });
  private readonly elementsMetaState$ = new BehaviorSubject<ElementsMeta | null>(null);
  readonly elementsMeta$ = this.elementsMetaState$.asObservable();
  readonly elementsPaginationConfig$ = combineLatest([this.elementsMeta$, this.elementPaginationRequest$]).pipe(
    map(
      ([meta, pagination]): PaginationConfig => ({
        currentPage: meta?.currentPage ?? pagination.page,
        itemsPerPage: meta?.itemsPerPage ?? pagination.limit,
        totalItems: meta?.totalItems ?? 0,
        pageSizes: [10, 20, 50],
      })
    )
  );

  /**
   * Drill-down path (root -> current parent).
   * - empty => show root elements (parentId null)
   * - last id => show direct children of that element
   * Only used for non-scalar dimensions.
   */
  drillPath$ = new BehaviorSubject<Element[]>([]);

  // Elements shown in the table for the current dimension level and page.
  displayedElements$ = this.elements$.asObservable();

  canView$ = from(this.securitySvc.hasAccess('pol_View', null));
  canEdit$ = from(this.securitySvc.hasAccess('pol_Edit', null));

  // Disable dragging when search is active to prevent sortOrder corruption
  canDragElements$ = combineLatest([this.canEdit$, this.elementSearchTerm$]).pipe(
    map(([canEdit, searchTerm]) => canEdit && !searchTerm.trim())
  );

  private readonly policyScopedSystemDimensionTitles = new Set(['coverage', 'term']);

  // Dimensions columns
  dimensionsColumns$: Observable<TableColumn<unknown>[]> = combineLatest([this.canView$, this.canEdit$]).pipe(
    map(([_canView, canEdit]) => {
      return [
        new TextColumn<Dimension>({ header: this.config.page.columnTitle }, item => ({
          text: item.title,
          classes: 'font-semibold',
        })),
        new TextColumn<Dimension>({ header: this.config.page.columnType, fitContent: true }, item => ({
          text: item.isScalar ? this.config.page.scalarType : this.config.page.categoricalType,
          classes: 'text-xs text-gray-600',
        })),
        new TextColumn<Dimension>({ header: 'Pricing', fitContent: true }, item => ({
          text: item.isPricing ? '✓' : '✗',
          classes: item.isPricing ? 'text-center text-success-600' : 'text-center text-gray-400',
        })),
        ...(canEdit
          ? [
              new IconButtonColumn<Dimension>({ stickyEnd: true, fitContent: true }, item => ({
                type: 'clear' as const,
                rounded: true,
                icon: 'edit-01' as const,
                disabled: item.isSystem === true,
                tooltipText: item.isSystem ? 'System-managed dimensions cannot be edited' : undefined,
                click: () => this.editDimension(item),
              })),
              new IconButtonColumn<Dimension>({ stickyEnd: true, fitContent: true }, item => ({
                type: 'clear' as const,
                rounded: true,
                icon: 'trash-01' as const,
                disabled: item.isSystem === true,
                tooltipText: item.isSystem ? 'System-managed dimensions cannot be deleted' : undefined,
                click: () => this.deleteDimension(item),
              })),
            ]
          : []),
      ];
    })
  );

  // Elements columns
  elementsColumns$: Observable<TableColumn<unknown>[]> = combineLatest([
    this.canView$,
    this.canEdit$,
    this.selectedDimension$,
  ]).pipe(
    map(([_canView, canEdit, selectedDimension]) => {
      const columns = [
        new TextColumn<Element>(
          {
            header: this.config.page.columnNumber,
            fitContent: true,
          },
          item => ({
            text: item.sortOrder?.toString() ?? '-',
            classes: 'text-gray-500 text-xs',
          })
        ),
        new TextColumn<Element>(
          {
            header: this.config.page.columnTitle,
          },
          item => ({
            text: item.title,
            classes: 'font-semibold',
          })
        ),

        ...(!selectedDimension?.isScalar
          ? [
              new IconButtonColumn<Element>({ fitContent: true }, item => {
                return {
                  type: 'clear' as const,
                  rounded: true,
                  icon: 'eye' as const,
                  tooltipText: 'View children',
                  click: () => this.viewChildren(item),
                };
              }),
            ]
          : []),

        ...(selectedDimension?.isScalar
          ? [
              new TextColumn<Element>(
                {
                  header: this.config.page.columnLower,
                  fitContent: true,
                },
                item => ({
                  text: item.lowerBound !== null && item.lowerBound !== undefined ? item.lowerBound.toString() : '-',
                  classes: 'text-gray-600 text-sm',
                })
              ),
              new TextColumn<Element>(
                {
                  header: this.config.page.columnUpper,
                  fitContent: true,
                },
                item => ({
                  text: item.upperBound !== null && item.upperBound !== undefined ? item.upperBound.toString() : '-',
                  classes: 'text-gray-600 text-sm',
                })
              ),
            ]
          : []),

        ...(canEdit
          ? [
              new IconButtonColumn<Element>({ stickyEnd: true, fitContent: true }, item => {
                const isSystemManagedDimension = this.areElementsLocked(selectedDimension);
                return {
                  type: 'clear' as const,
                  rounded: true,
                  icon: 'edit-01' as const,
                  disabled: isSystemManagedDimension,
                  tooltipText: isSystemManagedDimension
                    ? 'Elements of system-managed dimensions cannot be edited'
                    : undefined,
                  click: () => this.editElement(item),
                };
              }),
              new IconButtonColumn<Element>({ stickyEnd: true, fitContent: true }, item => {
                const isSystemManagedDimension = this.areElementsLocked(selectedDimension);
                return {
                  type: 'clear' as const,
                  rounded: true,
                  icon: 'trash-01' as const,
                  disabled: isSystemManagedDimension,
                  tooltipText: isSystemManagedDimension
                    ? 'Elements of system-managed dimensions cannot be deleted'
                    : undefined,
                  click: () => this.deleteElement(item),
                };
              }),
            ]
          : []),
      ];

      return columns;
    })
  );

  // Get pagination metadata from the table
  get dimensionsMeta$(): Observable<DimensionsMeta | null> {
    return (
      (this.dimensionsTable?.dataSource?.meta$ as Observable<DimensionsMeta> | undefined) ??
      (of(null) as unknown as Observable<DimensionsMeta | null>)
    );
  }

  get drillPath(): Element[] {
    return this.drillPath$.value;
  }

  ngOnInit(): void {
    this.policy$.pipe(takeUntil(this.destroy$)).subscribe(policy => {
      this.currentPolicyId$.next(policy?.id ?? null);
    });

    this.elementSearchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        const normalizedSearchTerm = searchTerm.trim();
        if (normalizedSearchTerm === this.elementSearchTerm$.value) {
          return;
        }

        this.elementSearchTerm$.next(normalizedSearchTerm);
        this.setElementsPagination({ page: 1 });
      });

    combineLatest([
      this.selectedDimension$,
      this.elementPaginationRequest$,
      this.elementSearchTerm$,
      this.drillPath$,
      this.currentPolicyId$,
      this.elementsRefresh$,
    ])
      .pipe(
        switchMap(([selectedDimension, pagination, searchTerm, drillPath, currentPolicyId]) => {
          if (!selectedDimension) {
            return of<ElementsLoadResult>({ status: 'cleared' });
          }

          const parentId = !selectedDimension.isScalar && drillPath.length ? drillPath[drillPath.length - 1].id : null;

          const params = this.buildElementRequestParams(
            selectedDimension.id,
            selectedDimension,
            pagination,
            currentPolicyId,
            {
              search: searchTerm,
              parentId,
            }
          );

          if (!params) {
            return of<ElementsLoadResult>({ status: 'blocked', pagination });
          }

          this.loadingElements$.next(true);

          return this.http.get<ElementsResponse>(`/v1/rating/elements`, { params }).pipe(
            map(
              response =>
                ({
                  status: 'loaded',
                  response,
                  pagination,
                }) as ElementsLoadResult
            ),
            catchError(error =>
              of<ElementsLoadResult>({
                status: 'error',
                error,
                pagination,
              })
            )
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        if (result.status === 'cleared') {
          this.elements$.next([]);
          this.elementsMetaState$.next(null);
          this.loadingElements$.next(false);
          return;
        }

        if (result.status === 'blocked') {
          this.elements$.next([]);
          this.elementsMetaState$.next(this.normalizeElementsMeta(undefined, result.pagination));
          this.loadingElements$.next(false);
          return;
        }

        if (result.status === 'loaded') {
          this.elements$.next(result.response.items || []);
          this.elementsMetaState$.next(this.normalizeElementsMeta(result.response.meta, result.pagination));
          this.sanitizeDrillPath();
          this.loadingElements$.next(false);
          return;
        }

        const errorMessage =
          typeof result.error === 'object' && result.error !== null && 'message' in result.error
            ? String((result.error as { message?: unknown }).message ?? '')
            : '';

        this.elements$.next([]);
        this.elementsMetaState$.next(this.normalizeElementsMeta(undefined, result.pagination));
        this.toastSvc.show({
          type: 'error',
          title: this.config.page.toasts.loadElementsError.title,
          text: errorMessage || this.config.page.toasts.loadElementsError.defaultText,
        });
        this.loadingElements$.next(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Update filter when type dropdown changes
  onTypeFilterChange(filterValue: 'all' | 'scalar' | 'categorical') {
    this.dimensionTypeFilter = filterValue;
    const filters: Record<string, string | boolean> = {};
    if (filterValue === 'scalar') {
      filters['isScalar'] = true;
    } else if (filterValue === 'categorical') {
      filters['isScalar'] = false;
    }
    this.dimensionsTable?.dataSource?.setQueryParams(filters);
    this.dimensionsTable?.dataSource?.setPage(1); // Reset to first page on filter
  }

  // Search dimensions
  searchDimensions(searchTerm: string) {
    const filters: Record<string, string> = {};
    if (searchTerm.trim()) {
      filters['search'] = searchTerm;
    }
    this.dimensionsTable?.dataSource?.setQueryParams(filters);
    this.dimensionsTable?.dataSource?.setPage(1); // Reset to first page on search
  }

  // Update element search filter
  filterElements(searchTerm: string) {
    this.elementSearchInputValue = searchTerm;
    this.elementSearchInput$.next(searchTerm);
  }

  // Trigger a reload for the currently selected dimension
  loadElements(dimensionId: string) {
    const selectedDimension = this.selectedDimension$.value;
    if (!selectedDimension || selectedDimension.id !== dimensionId) {
      return;
    }

    this.elementsRefresh$.next(this.elementsRefresh$.value + 1);
  }

  // Select a dimension to show its elements
  selectDimension(dimension: Dimension) {
    this.selectedDimension$.next(null);
    this.elements$.next([]);
    this.elementsMetaState$.next(null);
    this.elementSearchInputValue = '';
    this.elementSearchInput$.next('');
    this.elementSearchTerm$.next(''); // Reset search when switching
    this.resetDrill(false);
    this.setElementsPagination({ page: 1 });
    this.selectedDimension$.next(dimension);
  }

  // Deselect the current dimension
  clearSelection() {
    this.selectedDimension$.next(null);
    this.elements$.next([]);
    this.elementsMetaState$.next(null);
    this.elementSearchInputValue = '';
    this.elementSearchInput$.next('');
    this.elementSearchTerm$.next(''); // Reset search
    this.resetDrill(false);
    this.setElementsPagination({ page: 1 });
  }

  // Dimension CRUD
  createDimension() {
    this.dimensionDialog.open();
  }

  editDimension(dimension: Dimension) {
    if (dimension.isSystem) {
      return;
    }
    this.dimensionDialog.open(dimension);
  }

  deleteDimension(dimension: Dimension) {
    if (dimension.isSystem) {
      return;
    }
    this.deleteDimensionDialog.open(dimension);
  }

  onDimensionDialogClosed(success: boolean) {
    if (success) {
      this.dimensionsTable?.dataSource?.refresh();
    }
  }

  onDeleteDimensionDialogClosed(success: boolean) {
    if (success) {
      // If we deleted the selected dimension, clear the selection
      const selectedDimension = this.selectedDimension$.value;
      if (selectedDimension) {
        this.selectedDimension$.next(null);
        this.elements$.next([]);
        this.elementsMetaState$.next(null);
        this.elementSearchInputValue = '';
        this.elementSearchInput$.next('');
        this.resetDrill(false);
        this.elementSearchTerm$.next('');
        this.setElementsPagination({ page: 1 });
      }
      this.dimensionsTable?.dataSource?.refresh();
    }
  }

  // Element CRUD
  createElement() {
    const selectedDimension = this.selectedDimension$.value;

    if (!selectedDimension) {
      this.toastSvc.show({
        type: 'warning',
        title: this.config.page.toasts.noDimensionSelected.title,
        text: this.config.page.toasts.noDimensionSelected.text,
      });
      return;
    }

    if (this.areElementsLocked(selectedDimension)) {
      return;
    }

    const currentElements = this.elements$.value;

    let parentId: string | null = null;
    let parentTitle: string | null = null;

    if (!selectedDimension.isScalar) {
      const path = this.drillPath$.value ?? [];
      const currentParent = path.length ? path[path.length - 1] : null;

      parentId = currentParent?.id ?? null;
      parentTitle = currentParent?.title ?? null;
    }

    this.elementDialog.open(undefined, selectedDimension, currentElements, parentId, parentTitle);
  }

  editElement(element: Element) {
    const selectedDimension = this.selectedDimension$.value;
    if (!selectedDimension) return;
    const currentElements = this.elements$.value;
    this.elementDialog.open(element, selectedDimension, currentElements);
  }

  deleteElement(element: Element) {
    const selectedDimension = this.selectedDimension$.value;
    if (!selectedDimension) return;

    const params = this.buildElementRequestParams(
      selectedDimension.id,
      selectedDimension,
      { page: 1, limit: 1 },
      this.currentPolicyId$.value,
      { parentId: element.id, search: '' }
    );

    if (!params) {
      this.deleteElementDialog.open(element, false);
      return;
    }

    this.http.get<ElementsResponse>(`/v1/rating/elements`, { params }).subscribe({
      next: response => {
        const childCount = response.meta?.totalItems ?? response.items?.length ?? 0;
        this.deleteElementDialog.open(element, childCount > 0);
      },
      error: () => {
        this.deleteElementDialog.open(element, false);
      },
    });
  }

  onElementDialogClosed(success: boolean) {
    if (success) {
      this.reloadSelectedElements();
    }
  }

  onDeleteElementDialogClosed(success: boolean) {
    if (success) {
      this.reloadSelectedElements();
    }
  }

  onElementsPaginationChange(event: { currentPage: number; itemsPerPage: number }) {
    this.setElementsPagination({ page: event.currentPage, limit: event.itemsPerPage });
  }

  // Save new order after drag & drop
  reorderElements(reorderedVisible: Element[]) {
    const selectedDimension = this.selectedDimension$.value;
    if (!selectedDimension) return;

    let currentParentId: string | null = null;

    if (!selectedDimension.isScalar) {
      const path = this.drillPath$.value ?? [];
      currentParentId = path.length ? path[path.length - 1].id : null;
    }

    const all = [...this.elements$.value];

    const siblingIds = new Set(all.filter(e => (e.parentId ?? null) === (currentParentId ?? null)).map(e => e.id));

    const orderedSiblings = reorderedVisible.filter(e => siblingIds.has(e.id));

    const pagination = this.elementPaginationRequest$.value;
    const pageOffset = (pagination.page - 1) * pagination.limit;
    const newOrderById = new Map<string, number>();
    orderedSiblings.forEach((e, index) => newOrderById.set(e.id, pageOffset + index + 1));

    const merged = all.map(e => {
      const next = newOrderById.get(e.id);
      return next != null ? { ...e, sortOrder: next } : e;
    });

    this.elements$.next(merged);

    // Patch only the siblings that changed
    const updateRequests = orderedSiblings.map(e =>
      this.http.patch(`/v1/rating/elements/${e.id}`, { sortOrder: newOrderById.get(e.id) })
    );

    forkJoin(updateRequests).subscribe({
      next: () => {
        this.toastSvc.show({
          type: 'success',
          title: 'Elements reordered',
          text: 'Element order has been successfully updated.',
        });
      },
      error: (error: { error?: { message?: string } }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Reorder failed',
          text: error?.error?.message || 'Unable to update element order. Please try again.',
        });

        const dim = this.selectedDimension$.value;
        if (dim) this.loadElements(dim.id);
      },
    });
  }

  isPolicyScopedSystemDimension(dimension: Dimension | null): boolean {
    if (!dimension?.isSystem || !dimension.title) {
      return false;
    }
    return this.policyScopedSystemDimensionTitles.has(dimension.title.trim().toLowerCase());
  }

  /** Whether the dimension's elements are system-managed (synced) and cannot be added, edited, or deleted. */
  areElementsLocked(dimension: Dimension | null): boolean {
    return dimension?.elementsLocked === true;
  }

  /** Drill into an element's children. */
  viewChildren(parent: Element) {
    const selectedDimension = this.selectedDimension$.value;
    if (selectedDimension?.isScalar) return;
    this.drillPath$.next([...this.drillPath$.value, parent]);
    this.setElementsPagination({ page: 1 });
  }

  /** Go one level up in drill-down. */
  goUp() {
    const path = this.drillPath$.value;
    if (!path.length) return;
    this.drillPath$.next(path.slice(0, -1));
    this.setElementsPagination({ page: 1 });
  }

  /** Jump to an ancestor in the drill path (breadcrumb click). */
  jumpTo(parentId: string) {
    const path = this.drillPath$.value;
    const idx = path.findIndex(p => p.id === parentId);
    if (idx < 0) return;
    this.drillPath$.next(path.slice(0, idx + 1));
    this.setElementsPagination({ page: 1 });
  }

  /** Reset drill-down back to roots. */
  resetDrill(reload = true) {
    this.drillPath$.next([]);
    if (reload) {
      this.setElementsPagination({ page: 1 });
    }
  }

  /** Clear stale drill state when the selected dimension does not support hierarchy. */
  private sanitizeDrillPath() {
    const selectedDimension = this.selectedDimension$.value;
    if (selectedDimension?.isScalar) {
      if (this.drillPath$.value.length) {
        this.drillPath$.next([]);
      }
    }
  }

  private setElementsPagination(pagination: { page?: number; limit?: number }) {
    const current = this.elementPaginationRequest$.value;
    const next = {
      page: pagination.page ?? current.page,
      limit: pagination.limit ?? current.limit,
    };

    if (next.page !== current.page || next.limit !== current.limit) {
      this.elementPaginationRequest$.next(next);
    }
  }

  private reloadSelectedElements(pagination?: { page?: number; limit?: number }) {
    const selectedDimension = this.selectedDimension$.value;
    if (!selectedDimension) return;

    if (pagination) {
      this.setElementsPagination(pagination);
    }

    this.loadElements(selectedDimension.id);
  }

  private getCurrentElementParentId(): string | null {
    const path = this.drillPath$.value ?? [];
    return path.length ? path[path.length - 1].id : null;
  }

  private buildElementRequestParams(
    dimensionId: string,
    dimension: Dimension | null,
    pagination: { page: number; limit: number },
    currentPolicyId: string | null,
    options?: { parentId?: string | null; search?: string }
  ): Record<string, string> | null {
    const shouldScopeToPolicy = this.isPolicyScopedSystemDimension(dimension);
    const params: Record<string, string> = {
      dimensionId,
      page: pagination.page.toString(),
      limit: pagination.limit.toString(),
    };

    if (shouldScopeToPolicy && currentPolicyId) {
      params['policyId'] = currentPolicyId;
    } else if (shouldScopeToPolicy && !currentPolicyId) {
      this.toastSvc.show({
        type: 'error',
        title: this.config.page.toasts.loadElementsError.title,
        text: 'Select a policy before loading Coverage or Term elements.',
      });
      return null;
    }

    const search =
      options && Object.prototype.hasOwnProperty.call(options, 'search')
        ? (options.search?.trim() ?? '')
        : this.elementSearchTerm$.value.trim();

    if (search) {
      params['search'] = search;
    }

    if (!dimension?.isScalar) {
      const parentId =
        options && Object.prototype.hasOwnProperty.call(options, 'parentId')
          ? (options.parentId ?? null)
          : this.getCurrentElementParentId();

      params['parentId'] = parentId ?? 'null';
    }

    return params;
  }

  private normalizeElementsMeta(
    meta: Partial<ElementsMeta> | undefined,
    pagination: { page: number; limit: number }
  ): ElementsMeta {
    return {
      currentPage: meta?.currentPage ?? pagination.page,
      itemsPerPage: meta?.itemsPerPage ?? pagination.limit,
      totalItems: meta?.totalItems ?? 0,
    };
  }
}
